import {
  DownwardOutlineIcon,
  InfoOutlineIcon,
  UpwardOutlineIcon,
} from '@carboncopyco/icons';
import {
  ColorHex,
  TextBase,
  TextCaption,
  TextPageHeader,
  Tooltip,
  TooltipBubble,
} from '@carboncopyco/ui';
import type { SeriesOptionsType } from 'highcharts';
import { type ReactNode, isValidElement, useRef } from 'react';
import { LineChart, getChartOptions } from '../../LineChart';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { KpiLineChartSkeleton } from '../KpiLineChart/KpiLineChartSkeleton';

const skeletonClasses = 'bg-ds-natural-150 rounded-sm h-2 animate-pulse';

type OverviewChartBareProps = {
  name?: string;
  subtitle: string;
  tooltipText?: string;
  description: string | ReactNode;
  kpiNumber: number | string;
  kpiRawNumber?: number | string;
  kpiPercentage?: number | string;
  negative?: boolean;
  units?: string;
  lineChartData?: {
    values: number[];
    timestamps: string[];
  };
  isLoading?: boolean;
  color?: SeriesOptionsType['color'];
  isError?: boolean;
  isEmpty?: boolean;
};

export const ActivityLineChart = ({
  isEmpty,
  isError,
  isLoading,
  subtitle,
  tooltipText,
  lineChartData,
  name,
  color,
  kpiNumber,
  kpiRawNumber,
  kpiPercentage,
  description,
  units,
  negative,
}: OverviewChartBareProps) => {
  const infoRef = useRef<HTMLDivElement>(null);

  if (isEmpty) {
    return (
      <div className="flex justify-center items-center grow">
        <EmptyState />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex justify-center items-center grow">
        <ErrorState />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className={`${skeletonClasses} w-10 mb-6`} />
        <div className={`${skeletonClasses} w-24 mb-6`} />
        <KpiLineChartSkeleton />
      </div>
    );
  }

  return (
    <>
      <div className="flex gap-2 items-center mb-5">
        <TextBase
          label={subtitle}
          color="text-ds-natural-600"
        />
        <div ref={infoRef}>
          <InfoOutlineIcon
            size={16}
            color={ColorHex.natural600}
          />
        </div>
        <Tooltip
          interactive={false}
          placement="bottom"
          anchorRef={infoRef}
          autoCalcArrowPosition
        >
          <div className="px-4 py-2 w-ds-58 leading-4">
            <TextCaption
              label={tooltipText}
              color="text-ds-natural-300"
            />
          </div>
        </Tooltip>
      </div>
      <div className="flex flex-col">
        <TooltipBubble
          placement="top-start"
          title={String(kpiRawNumber)}
        >
          <TextPageHeader
            label={String(kpiNumber)}
            color="text-ds-natural-600"
          />
        </TooltipBubble>
        {units && (
          <div className="mb-0.5 ml-1.5">
            <TextCaption
              label={units}
              color="text-ds-natural-300"
            />
          </div>
        )}
        <div className="w-full max-w-[400px]">
          {lineChartData && (
            <LineChart
              options={getChartOptions({
                data: lineChartData.values,
                xCategories: lineChartData.timestamps,
                name: name || '',
                color,
                extraOptions: {
                  chart: {
                    spacing: [10, 10, 15, 10],
                    height: 80,
                  },
                },
              })}
            />
          )}
        </div>
        <div className="flex items-center mt-1 gap-1 ">
          {!!kpiPercentage && (
            <div className="flex items-center gap-1">
              {negative ? (
                <DownwardOutlineIcon
                  size={12}
                  color={ColorHex.danger300}
                />
              ) : (
                <UpwardOutlineIcon
                  size={12}
                  color={ColorHex.accent2400}
                />
              )}
              <TextCaption
                label={`${String(kpiPercentage)}%`}
                color={negative ? 'text-ds-danger-300' : 'text-ds-accent2-400'}
              />
            </div>
          )}
          <div className="whitespace-nowrap flex items-center">
            {isValidElement(description) ? (
              description
            ) : (
              <TextCaption
                label={description}
                color="text-ds-natural-300"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
